import { graphql } from "gatsby"
import _ from "lodash"
import React from "react"
import Footer from "../../../components/shared/footer"
import useSiteMetadata from "../../../components/shared/hooks/useSiteMetadata"
import Layout from "../../../components/shared/layout"
import SEO from "../../../components/shared/seo"
import FixedHeaderSpacer from "../../atoms/fixed-header-spacer"
import { IArticle } from "../../molecules/article-card"
import FeaturedArticles from "../../molecules/featured-articles"
import {
  contentfulBlogPostToArticleCard,
  contentfulComposePageToArticleCard,
} from "../../util/contentful-data"
import InternalRoutes from "../../util/internal-routes"
import Sections from "../section"
import ArticleFeatureBlocks from "./article-feature-blocks"
import EditorsPick, { EDITORS_PICK_TYPENAME } from "./editors-pick"
import ExploreTopics from "./explore-topics"
import LatestMedia from "./latest-media"

/**
 * Cleans the latest media articles into key value pairs for easy retrieval
 * @param currentCategory - the current page's category
 * @param latestForCategory - the latest articles for the current category
 * @param groupedByTopic - the latest articles grouped by topic
 * @returns Categorized object `{ <Category>: [ <Topic>: [Articles] ] }`
 */
const categorizeLatestMediaArticles = (
  currentCategory,
  latestForCategory,
  groupedByTopic
) => {
  if (currentCategory === "All") {
    return {
      All: latestForCategory.edges.map(
        (article): IArticle => contentfulBlogPostToArticleCard(article.node)
      ),
    }
  }
  return Object.assign(
    {
      All: latestForCategory.edges.map(
        (article): IArticle => contentfulBlogPostToArticleCard(article.node)
      ),
    },
    ...groupedByTopic.group.map((topic) => {
      return {
        [topic.fieldValue]: topic.edges.map((article) =>
          contentfulBlogPostToArticleCard(article.node)
        ),
      }
    })
  )
}

/**
 * Template for the blog home
 */
const BlogHomePage = ({
  location,
  pageContext,
  data: {
    articlesGroupedByTopic,
    latest,
    latestCompanyPosts,
    latestDevelopersPosts,
    featuredPdf,
    featuredNewsletter,
    contentfulWebsitePage,
  },
}) => {
  // Organize the tags and add slug
  const tags = pageContext.tags.reduce(
    (dict, category) => ({
      ...dict,
      [category.fieldValue]: category.group.map((topic) => {
        return {
          name: topic.fieldValue,
          slug: _.kebabCase(topic.fieldValue.toLowerCase()),
        }
      }),
    }),
    {}
  )

  // Get the latest media articles
  const latestMediaArticlesData = categorizeLatestMediaArticles(
    pageContext.name,
    latest,
    articlesGroupedByTopic
  )

  // First section is a special case where we use the ContentfulFeaturedCards content type
  // but map it to the editors pick
  const editorsPick =
    contentfulWebsitePage.components?.length >= 1 &&
    contentfulWebsitePage.components[0].__typename === EDITORS_PICK_TYPENAME

  // If there is an editors pick, we need to replace the 480px image with the higher definition 1440px version
  editorsPick &&
    (contentfulWebsitePage.components[0].cards[0].content.image.fluid =
      contentfulWebsitePage.components[0].cards[0].content.image.fluidHd)

  // If there was an editors pick, start the components at element [1]
  // otherwise start at element [0]
  const pageComponents =
    editorsPick && contentfulWebsitePage.components?.length > 1
      ? contentfulWebsitePage.components.slice(1)
      : contentfulWebsitePage.components

  const metadata = useSiteMetadata()

  return (
    <Layout showBackground={true} contactLightbox={true} location={location}>
      <SEO
        title={contentfulWebsitePage?.seoTitle || metadata.title}
        description={
          contentfulWebsitePage?.seoDescription?.seoDescription ||
          metadata.description
        }
      />
      <FixedHeaderSpacer />
      {editorsPick && (
        <EditorsPick
          title={contentfulWebsitePage.components[0].title}
          article={contentfulComposePageToArticleCard(
            contentfulWebsitePage.components[0].cards[0]
          )}
        />
      )}
      <Sections components={pageComponents || []} website="Blog" />
      <ArticleFeatureBlocks features={[featuredNewsletter, featuredPdf]} />
      <LatestMedia
        tags={tags}
        articles={latestMediaArticlesData}
        currentTab={pageContext.currentPage}
        currentCategory={{
          name: pageContext.categoryName,
          slug: pageContext.categorySlug,
        }}
      />
      {latestDevelopersPosts.nodes &&
        latestDevelopersPosts.nodes.length > 0 && (
          <FeaturedArticles.Blog
            title={"For Developers"}
            url={InternalRoutes.Blog({ slug: "developers" })}
            cards={latestDevelopersPosts.nodes}
          />
        )}
      {latestCompanyPosts.nodes && latestCompanyPosts.nodes.length > 0 && (
        <FeaturedArticles.Blog
          title={"Company"}
          url={InternalRoutes.Blog({ slug: "company" })}
          cards={latestCompanyPosts.nodes}
        />
      )}
      <ExploreTopics />
      <Footer />
    </Layout>
  )
}

export default BlogHomePage

export const BlogHomePageQuery = graphql`
  query (
    $categorySlug: String
    $categoryFilter: ContentfulBlogPostFilterInput
  ) {
    articlesGroupedByTopic: allContentfulBlogPost(
      sort: { fields: publishDate, order: DESC }
      filter: {
        tags: { category: { slug: { eq: $categorySlug } } }
        compose__page: { elemMatch: { title: { ne: null } } }
      }
    ) {
      group(field: tags___topic___name, limit: 6) {
        edges {
          node {
            ...ContentfulBlogPostFragment
          }
        }
        fieldValue
      }
    }
    latest: allContentfulBlogPost(
      sort: { fields: publishDate, order: DESC }
      filter: $categoryFilter
      limit: 6
    ) {
      edges {
        node {
          ...ContentfulBlogPostFragment
        }
      }
    }
    latestCompanyPosts: allContentfulComposePage(
      filter: { content: { tags: { category: { slug: { eq: "company" } } } } }
      limit: 3
      sort: { fields: content___publishDate, order: DESC }
    ) {
      nodes {
        title
        slug
        content {
          ...ContentfulBlogPostFragment
        }
      }
    }
    latestDevelopersPosts: allContentfulComposePage(
      filter: {
        content: { tags: { category: { slug: { eq: "developers" } } } }
      }
      limit: 3
      sort: { fields: content___publishDate, order: DESC }
    ) {
      nodes {
        title
        slug
        content {
          ...ContentfulBlogPostFragment
        }
      }
    }
    featuredPdf: contentfulArticleFeature(
      internalName: { eq: "Mappedin Info Pack PDF Download" }
    ) {
      ...ArticleFeatureFragment
    }
    featuredNewsletter: contentfulArticleFeature(
      internalName: { eq: "Mappedin Newsletter Subscription" }
    ) {
      ...ArticleFeatureFragment
    }
    contentfulWebsitePage(website: { eq: "Blog" }, slug: { eq: "index" }) {
      seoTitle
      seoDescription {
        seoDescription
      }
      components {
        __typename
        ... on ContentfulFeaturedCards {
          contentful_id
          title
          cards {
            title
            slug
            content {
              ...ContentfulBlogPostFragment
              image {
                # For editors pick get a larger image
                fluidHd: fluid(maxWidth: 1440) {
                  ...GatsbyContentfulFluid
                }
              }
            }
          }
        }
      }
    }
  }
`
